import { useEffect, useRef, useState } from 'react';

interface ProgressArcProps {
  value: number;
  /* Minimum value for percentage calcuation (default is 0)*/
  minValue?: number;
  /* Maximum value for percentage calculation*/
  maxValue: number;
  /* The color of the arc*/
  color?: string;
  /* The color of the arc track (default is faded color prop)*/
  backgroundColor?: string;
  /* The length of the arc segment (in degrees)*/
  arcLength?: number;
  /* The starting position of the arc segment (in degrees)*/
  arcStart?: number;
  /* The width / thickness of the arc segment (in SVG units, scales with component)*/
  arcWidth?: number;
  /* The radius as a percentage of the component width (0-100)*/
  radius?: number;
  /* Reverse the direction of the arc (default is clockwise)*/
  reversed?: boolean;
  /* Vertical alignment of the value label (default is middle)*/
  labelPosition?: 'top' | 'middle' | 'bottom';
  /* Custom label formatter */
  formatLabel?: (value: number) => React.ReactNode;
  /* Number of sections to divide the arc into (renders subdivisions - 1 dots) */
  subdivisions?: number;
  /* Color of the subdivision dots (default is white) */
  subdivisionColor?: string;
}

export const ProgressArc = ({
  value,
  minValue = 0,
  maxValue,
  arcLength = 360,
  arcStart = 0,
  arcWidth = 10,
  radius = 90,
  reversed = false,
  labelPosition = 'middle',
  formatLabel,
  subdivisions,
  subdivisionColor = 'white',
  color,
  backgroundColor,
}: ProgressArcProps) => {
  const [percentage, setPercentage] = useState(0);
  const [displayedColor, setDisplayedColor] = useState('#ccc');
  const [containerWidth, setContainerWidth] = useState(200);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    // Animate value
    const timeout = setTimeout(() => {
      if (value > 0) {
        setPercentage(
          Math.min(100, Math.max(0, Math.round(((value - minValue) / (maxValue - minValue)) * 100)))
        );
        if (color) setDisplayedColor(color);
      }
    }, 100);

    // Clean up the timeout
    return () => clearTimeout(timeout);
  }, [value, maxValue, minValue, color]);

  const COORDS = 200;
  const center = COORDS / 2;
  const pixelRadius = (radius / 100) * center;
  const scaledArcWidth = arcWidth * (COORDS / containerWidth);
  const circumference = 2 * Math.PI * pixelRadius;
  const arcLengthPx = (arcLength / 360) * circumference;
  const percentageFillPx = (percentage / 100) * arcLengthPx;
  const rotationDeg = arcStart - 90;

  const isAngleInArc = (angleDeg: number) => {
    const offset = (((angleDeg - rotationDeg) % 360) + 360) % 360;
    return offset <= arcLength;
  };
  const hasBottomArc = isAngleInArc(90);
  const hasTopArc = isAngleInArc(270);
  const middleOffset = (arcWidth / 2) * ((hasTopArc ? 1 : 0) - (hasBottomArc ? 1 : 0));

  const getAxisBounds = (
    projectFn: (deg: number) => number,
    maxExtremeAngle: number,
    minExtremeAngle: number
  ) => {
    let min = Math.min(projectFn(rotationDeg), projectFn(rotationDeg + arcLength));
    let max = Math.max(projectFn(rotationDeg), projectFn(rotationDeg + arcLength));
    if (isAngleInArc(maxExtremeAngle)) max = center + pixelRadius;
    if (isAngleInArc(minExtremeAngle)) min = center - pixelRadius;
    return { start: min - scaledArcWidth / 2, size: max - min + scaledArcWidth };
  };

  const { start: viewBoxY, size: arcViewboxHeight } = getAxisBounds(
    (deg) => center + pixelRadius * Math.sin((deg * Math.PI) / 180),
    90,
    270
  );
  const { start: viewBoxX, size: arcViewboxWidth } = getAxisBounds(
    (deg) => center + pixelRadius * Math.cos((deg * Math.PI) / 180),
    0,
    180
  );

  return (
    <div className="progress-arc-container" ref={containerRef}>
      <span
        className={`progress-arc-label progress-arc-label--${labelPosition}`}
        style={
          labelPosition === 'top'
            ? { top: hasTopArc ? arcWidth : 0 }
            : labelPosition === 'bottom'
            ? { bottom: hasBottomArc ? arcWidth : 0 }
            : middleOffset !== 0
            ? { transform: `translateY(calc(-50% + ${middleOffset}px))` }
            : undefined
        }
      >
        {formatLabel ? formatLabel(value) : value}
      </span>
      <svg
        width="100%"
        viewBox={`${viewBoxX} ${viewBoxY} ${arcViewboxWidth} ${arcViewboxHeight}`}
        className="progress-arc"
        style={{ display: 'block' }}
      >
        <g transform={reversed ? `translate(${COORDS}, 0) scale(-1, 1)` : undefined}>
          <circle
            cx={center}
            cy={center}
            r={pixelRadius}
            fill="none"
            stroke={backgroundColor ? backgroundColor : displayedColor}
            style={!backgroundColor ? { opacity: 0.25 } : {}}
            strokeWidth={scaledArcWidth}
            strokeDasharray={`${arcLengthPx} ${circumference - arcLengthPx}`}
            strokeLinecap="round"
            transform={`rotate(${rotationDeg}, ${center}, ${center})`}
          />
          <circle
            cx={center}
            cy={center}
            r={pixelRadius}
            fill="none"
            stroke={displayedColor}
            strokeWidth={scaledArcWidth}
            strokeDasharray={`${percentageFillPx} ${circumference - percentageFillPx}`}
            strokeLinecap="round"
            transform={`rotate(${rotationDeg}, ${center}, ${center})`}
            style={{ transition: 'stroke-dasharray 0.5s ease' }}
          />
          {subdivisions &&
            subdivisions > 1 &&
            Array.from({ length: subdivisions - 1 }, (_, i) => {
              const angleDeg = rotationDeg + (arcLength / subdivisions) * (i + 1);
              const angleRad = (angleDeg * Math.PI) / 180;
              return (
                <circle
                  key={i}
                  cx={center + pixelRadius * Math.cos(angleRad)}
                  cy={center + pixelRadius * Math.sin(angleRad)}
                  r={scaledArcWidth * 0.3}
                  fill={subdivisionColor}
                />
              );
            })}
        </g>
      </svg>
    </div>
  );
};
